/**
 * ActionInboxPage — the human review surface for engine proposals.
 * Every card shows the action, the engine's rationale, and its evidence
 * summary; approve executes through the same executor auto mode uses.
 * Shadow-stage proposals (manual campaigns) carry a badge so the
 * rollout ladder is visible. Safety actions appear as history — they
 * execute immediately and are never approval-gated.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdoptActionDto } from '@/lib/api';
import { ARCHE_PATH } from '../constants';
import {
  useAdoptActions,
  useAdoptAutoUnlock,
  useReviewAdoptAction,
} from '../hooks/useAdvertisingOptimization';

interface Props {
  holderId: string;
}

const STATUS_FILTERS = ['proposed', 'executed', 'failed', 'rejected', 'expired'] as const;

const STATUS_STYLES: Record<string, string> = {
  proposed: 'bg-sky-100 text-sky-800',
  approved: 'bg-sky-100 text-sky-800',
  executing: 'bg-amber-100 text-amber-800',
  executed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
  rejected: 'bg-muted text-muted-foreground',
  expired: 'bg-muted text-muted-foreground',
};

function actionTitle(action: AdoptActionDto): string {
  // A decided ad_creative experiment (e.g. a landing-page test): pauses the
  // losing variants' ads and completes the experiment.
  if (action.actionType === 'promote_winner' && action.targetType === 'experiment') {
    const winner = (action.params as { winnerVariantKey?: string }).winnerVariantKey;
    return winner ? `Promote experiment winner '${winner}'` : 'Promote experiment winner';
  }
  const labels: Record<string, string> = {
    kill_variant: 'Kill creative',
    promote_winner: 'Promote winning creative',
    scale_budget: 'Scale placement budget',
    reduce_budget: 'Reduce placement budget',
    request_variants: 'Generate challenger drafts',
    pause_campaign: 'Pause campaign (budget stop)',
    complete_campaign: 'Complete campaign',
    pause_placement: 'Pause placement',
  };
  return labels[action.actionType] ?? action.actionType.replace(/_/g, ' ');
}

function EvidenceSummary({ action }: { action: AdoptActionDto }) {
  const e = action.evidence as {
    pBest?: number;
    pWorst?: number;
    metricWindowDays?: number;
    spentTotalCents?: number;
    totalBudgetCents?: number;
  };
  const bits: string[] = [];
  if (typeof e.pBest === 'number') bits.push(`P(best) ${(e.pBest * 100).toFixed(1)}%`);
  if (typeof e.pWorst === 'number') bits.push(`P(worst) ${(e.pWorst * 100).toFixed(1)}%`);
  if (typeof e.metricWindowDays === 'number') bits.push(`${e.metricWindowDays}d window`);
  if (typeof e.spentTotalCents === 'number' && typeof e.totalBudgetCents === 'number') {
    bits.push(
      `$${(e.spentTotalCents / 100).toFixed(2)} of $${(e.totalBudgetCents / 100).toFixed(2)} spent`,
    );
  }
  if (bits.length === 0) return null;
  return <div className="text-xs text-muted-foreground">{bits.join(' · ')}</div>;
}

function ActionCard({ action, holderId }: { action: AdoptActionDto; holderId: string }) {
  const review = useReviewAdoptAction(holderId);
  const rolloutStage = (action.evidence as { rolloutStage?: string }).rolloutStage;
  const reviewable = action.status === 'proposed' && action.actionClass === 'optimization';

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{actionTitle(action)}</span>
          <span
            className={`text-xs rounded px-1.5 py-0.5 ${STATUS_STYLES[action.status] ?? 'bg-muted'}`}
          >
            {action.status}
          </span>
          {action.actionClass === 'safety' && (
            <span
              className="text-xs rounded px-1.5 py-0.5 bg-rose-100 text-rose-800"
              title="Safety actions execute immediately and are never approval-gated"
            >
              safety
            </span>
          )}
          {rolloutStage === 'shadow' && (
            <span
              className="text-xs rounded px-1.5 py-0.5 bg-violet-100 text-violet-800"
              title="This campaign is in manual (shadow) mode: the engine logs what it would do; nothing moves without your approval"
            >
              shadow
            </span>
          )}
        </div>
        {reviewable && (
          <div className="flex gap-1.5">
            <button
              onClick={() => review.mutate({ actionId: action.id, verb: 'approve' })}
              disabled={review.isPending}
              className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50"
            >
              Approve
            </button>
            <button
              onClick={() => review.mutate({ actionId: action.id, verb: 'reject' })}
              disabled={review.isPending}
              className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        )}
      </div>

      <p className="text-sm">{action.rationale}</p>
      <EvidenceSummary action={action} />

      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        <Link
          to={`${ARCHE_PATH}/campaigns/${action.campaignId}`}
          className="text-primary hover:underline"
        >
          campaign {action.campaignId.slice(-6)}
        </Link>
        <span>{new Date(action.createdAt).toLocaleString()}</span>
        {action.status === 'proposed' && action.expiresAt && (
          <span>expires {new Date(action.expiresAt).toLocaleString()}</span>
        )}
        {action.executionError && <span className="text-rose-600">{action.executionError}</span>}
      </div>
      {review.isError && (
        <div className="text-xs text-rose-600">
          {review.error instanceof Error ? review.error.message : 'Review failed'}
        </div>
      )}
    </div>
  );
}

export function ActionInboxPage({ holderId }: Props) {
  const [status, setStatus] = useState<string>('proposed');
  const actionsQuery = useAdoptActions(holderId, { status });
  const autoUnlockQuery = useAdoptAutoUnlock(holderId);
  const actions = actionsQuery.data ?? [];
  const autoUnlock = autoUnlockQuery.data;

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Action Inbox</h1>
        {autoUnlock && (
          <span
            className={`text-xs rounded px-2 py-1 ${
              autoUnlock.unlocked
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-muted text-muted-foreground'
            }`}
            title={
              autoUnlock.unlocked
                ? autoUnlock.via === 'admin'
                  ? 'Auto mode was approved for this workspace by a platform administrator'
                  : 'Auto mode is unlocked for this workspace'
                : `Auto mode unlocks after ${autoUnlock.required} approved engine actions execute cleanly (${autoUnlock.executed}/${autoUnlock.required} so far${autoUnlock.failed > 0 ? `, ${autoUnlock.failed} failed` : ''})`
            }
          >
            {autoUnlock.unlocked
              ? 'auto mode unlocked'
              : `auto mode: ${autoUnlock.executed}/${autoUnlock.required} clean executions`}
          </span>
        )}
      </header>

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              status === s
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {actionsQuery.isLoading ? (
        <p className="text-muted-foreground">Loading actions…</p>
      ) : actions.length === 0 ? (
        <p className="text-muted-foreground">
          {status === 'proposed'
            ? 'No open proposals. The optimization engine runs hourly against active campaigns and files its suggestions here.'
            : `No ${status} actions.`}
        </p>
      ) : (
        actions.map((a) => <ActionCard key={a.id} action={a} holderId={holderId} />)
      )}
    </div>
  );
}
